import { useCallback, useEffect, useState } from "react";
import { useAssessmentSession } from "../contexts/assessmentUserContext";
import { getApiUrls } from "../lib/apiUrl";
import { fetchDistricts } from "../services/appDistrictService";
import { fetchGrades } from "../services/appGradeService";
import { fetchSchools } from "../services/appSchoolService";
import {
  listAssessmentBenchmarksRequest,
  type BenchmarkDetail,
  type BenchmarkSummary,
} from "../services/assessmentContentService";
import { primaryGrades, secondaryGrades } from "../pages/Benchmarks";
import { hasSameValues } from "../lib/helpers";

export type BenchmarkDashboardRow = BenchmarkSummary & {
  district_ids: string[];
  school_ids: string[];
  grade_ids: string[];
  districtNames: string[];
  schoolNames: string;
  gradeNames: string;
};

type UseBenchmarksDashboardResult = {
  rows: BenchmarkDashboardRow[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

type NamedOption = {
  id: string;
  name: string;
};

function formatScopeCount(count: number, singularLabel: string): string {
  return `${count} ${singularLabel}${count === 1 ? "" : "s"}`;
}

export function labelsFromIds(
  ids: string[],
  options: NamedOption[],
): string[] {
  return ids.map(
    (id) => options.find((option) => option.id === id)?.name ?? id,
  );
}

export function formatScopeLabels(
  labels: string[],
  singularLabel: string,
  emptyLabel = "-",
): string {
  if (labels.length === 0) {
    return emptyLabel;
  }

  if (labels.length > 1) {
    return formatScopeCount(labels.length, singularLabel);
  }

  return labels[0] || "";
}

export function formatLabelsFromIds(
  ids: string[],
  options: NamedOption[],
  singularLabel: string,
  emptyLabel = "-",
): string {
  return formatScopeLabels(
    labelsFromIds(ids, options),
    singularLabel,
    emptyLabel,
  );
}

export function formatGradeScopeFromIds(
  ids: string[],
  gradeOptions: NamedOption[],
): string {
  if (ids.length === 0) {
    return "All";
  }

  const labels = ids
    .map((id) => gradeOptions.find((option) => option.id === id)?.name)
    .filter((label): label is string => Boolean(label));

  if (labels.length === 0) {
    return "-";
  }

  const availableGradeLabels = gradeOptions.map((option) => option.name);
  const availablePrimaryGrades = primaryGrades.filter((grade) =>
    availableGradeLabels.includes(grade),
  );
  const availableSecondaryGrades = secondaryGrades.filter((grade) =>
    availableGradeLabels.includes(grade),
  );

  if (hasSameValues(labels, availableGradeLabels)) {
    return "All";
  }
  if (hasSameValues(labels, availablePrimaryGrades)) {
    return "Primary";
  }
  if (hasSameValues(labels, availableSecondaryGrades)) {
    return "Secondary";
  }

  if (labels.length === 1) {
    return labels[0] || "";
  }

  return formatScopeCount(ids.length, "grade");
}

function buildDashboardRow(
  benchmark: BenchmarkDetail,
  districtOptions: { id: string; name: string }[],
  schoolOptions: { id: string; name: string }[],
  gradeOptions: { id: string; name: string }[],
): BenchmarkDashboardRow {
  return {
    ...benchmark,
    district_ids: benchmark.district_ids ?? [],
    school_ids: benchmark.school_ids ?? [],
    grade_ids: benchmark.grade_ids ?? [],
    districtNames: labelsFromIds(
      benchmark.district_ids ?? [],
      districtOptions,
    ),
    schoolNames: formatLabelsFromIds(
      benchmark.school_ids ?? [],
      schoolOptions,
      "school",
    ),
    gradeNames: formatGradeScopeFromIds(
      benchmark.grade_ids ?? [],
      gradeOptions,
    ),
  };
}

export function useBenchmarksDashboard(): UseBenchmarksDashboardResult {
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();
  const [rows, setRows] = useState<BenchmarkDashboardRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRows = useCallback(
    async (signal?: AbortSignal) => {
      if (!token) {
        setRows([]);
        setError("Missing auth token.");
        setIsLoading(false);
        return;
      }
      if (!apiUrl) {
        setRows([]);
        setError("Assessments API URL is not configured.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const [benchmarks, districts] = await Promise.all([
          listAssessmentBenchmarksRequest({ apiUrl, token, signal }),
          fetchDistricts(token),
        ]);

        const districtIds = [
          ...new Set(
            benchmarks.flatMap((benchmark) => benchmark.district_ids ?? []),
          ),
        ];

        const [schools, grades] = await Promise.all([
          districtIds.length
            ? fetchSchools(token, { districtIds })
            : Promise.resolve([]),
          districtIds.length
            ? fetchGrades(token, { districtIds })
            : Promise.resolve([]),
        ]);

        const districtOptions = districts.map((district) => ({
          id: district.id,
          name: district.name,
        }));
        const schoolOptions = schools.map((school) => ({
          id: school.id,
          name: school.name,
        }));
        const gradeOptions = grades.map((grade) => ({
          id: grade.id,
          name: grade.grade,
        }));

        const nextRows = benchmarks.map((benchmark) =>
          buildDashboardRow(
            benchmark,
            districtOptions,
            schoolOptions,
            gradeOptions,
          ),
        );

        setRows(nextRows);
      } catch (loadError: unknown) {
        if (
          loadError instanceof DOMException &&
          loadError.name === "AbortError"
        ) {
          return;
        }
        setRows([]);
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Unable to load benchmarks.",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, token],
  );

  useEffect(() => {
    const controller = new AbortController();
    void Promise.resolve().then(() => loadRows(controller.signal));
    return () => {
      controller.abort();
    };
  }, [loadRows]);

  return {
    rows,
    isLoading,
    error,
    reload: () => loadRows(),
  };
}
